import React, { useState } from 'react';
import { X, UserPlus, Users, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls
const API = '/api';

const GuestListOnboarding = ({ isOpen, onClose, userId, onGuestAdded }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [guestData, setGuestData] = useState({
    name: '',
    email: '',
    phone: '',
    side: 'bride',
    plus_one: false
  });

  const handleAddGuest = async () => {
    if (!guestData.name.trim()) {
      toast.error('Please enter a guest name');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/guests`, guestData, {
        headers: { 'user-id': userId }
      });
      
      toast.success('First guest added! 🎉');
      onGuestAdded();
      onClose(true, false); // completed=true, skipped=false
    } catch (error) {
      toast.error('Failed to add guest');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose(false, true); // completed=false, skipped=true
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200 bg-gradient-to-r from-[#881337] to-[#9F1239]">
          <div className="flex items-center gap-3">
            <Users className="text-white" size={28} />
            <h2 className="text-2xl font-serif font-bold text-white">Let's Add Your First Guests!</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-stone-50 rounded-xl p-6 border-2 border-dashed border-stone-300">
                <h3 className="text-lg font-semibold text-[#1C1917] mb-3 flex items-center gap-2">
                  <Heart size={20} className="text-[#881337]" />
                  Getting Started with Your Guest List
                </h3>
                <div className="space-y-3 text-sm text-stone-700">
                  <p><strong>Start with those closest to you:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Parents and immediate family</li>
                    <li>Wedding party (bridesmaids & groomsmen)</li>
                    <li>Best friends who'll definitely attend</li>
                  </ul>
                  <p className="text-stone-500 italic mt-3">
                    💡 Don't worry about adding everyone at once - you can always add more guests later!
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  <strong>Quick Tip:</strong> For each guest, you can optionally add email, phone, and whether they get a plus-one. The more info you add now, the easier managing RSVPs will be later!
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSkip}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
                >
                  Skip for Now
                </Button>
                <Button
                  onClick={() => setStep(2)}
                  className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white"
                >
                  Add My First Guest
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <p className="text-stone-600">
                Great! Let's add someone special to your guest list.
              </p>

              <div>
                <label className="text-[#1C1917] font-medium mb-2 block">
                  Guest Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={guestData.name}
                  onChange={(e) => setGuestData({ ...guestData, name: e.target.value })}
                  placeholder="e.g., Sarah Johnson, Mom & Dad Smith"
                  className="border-stone-300"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[#1C1917] font-medium mb-2 block">
                    Email <span className="text-stone-400">(optional)</span>
                  </label>
                  <Input
                    type="email"
                    value={guestData.email}
                    onChange={(e) => setGuestData({ ...guestData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="border-stone-300"
                  />
                </div>
                <div>
                  <label className="text-[#1C1917] font-medium mb-2 block">
                    Phone <span className="text-stone-400">(optional)</span>
                  </label>
                  <Input
                    value={guestData.phone}
                    onChange={(e) => setGuestData({ ...guestData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="border-stone-300"
                  />
                </div>
              </div>

              <div>
                <label className="text-[#1C1917] font-medium mb-2 block">Guest Side</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setGuestData({ ...guestData, side: 'bride' })}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      guestData.side === 'bride'
                        ? 'border-[#881337] bg-rose-50'
                        : 'border-stone-300 hover:border-stone-400'
                    }`}
                  >
                    <div className="text-center">
                      <Heart size={20} className="mx-auto mb-1 text-[#881337]" fill={guestData.side === 'bride' ? '#881337' : 'none'} />
                      <p className="text-sm font-medium">Bride's Side</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setGuestData({ ...guestData, side: 'groom' })}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      guestData.side === 'groom'
                        ? 'border-[#881337] bg-rose-50'
                        : 'border-stone-300 hover:border-stone-400'
                    }`}
                  >
                    <div className="text-center">
                      <Heart size={20} className="mx-auto mb-1 text-[#881337]" fill={guestData.side === 'groom' ? '#881337' : 'none'} />
                      <p className="text-sm font-medium">Groom's Side</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="plus_one"
                  checked={guestData.plus_one}
                  onChange={(e) => setGuestData({ ...guestData, plus_one: e.target.checked })}
                  className="w-4 h-4 text-[#881337] border-stone-300 rounded"
                />
                <label htmlFor="plus_one" className="text-sm text-stone-700">
                  This guest gets a plus-one
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
                >
                  Back
                </Button>
                <Button
                  onClick={handleAddGuest}
                  disabled={loading || !guestData.name.trim()}
                  className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white"
                >
                  {loading ? 'Adding...' : 'Add Guest'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestListOnboarding;
